import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { Nullable, UserWithToken, WithData, WithMeta } from './api.types';
import { Archetype } from './archetypes';

export enum MetaDeckTier {
  Tier0 = 'TIER_0',
  Tier1 = 'TIER_1',
  Tier15 = 'TIER_1_5',
  Tier2 = 'TIER_2',
  Tier3 = 'TIER_3',
  Tier4 = 'TIER_4',
}

/**
 * Banlist en la que el mazo esta listado. `formatId` viene para que el front
 * derive los formatos del mazo: no hay relacion MetaDeck<->Format, el formato
 * de un mazo es el de las banlists donde aparece.
 */
export type MetaDeckBanlist = {
  id: number;
  name: string;
  effectiveDate: string;
  isActive: boolean;
  formatId: number;
  tier: MetaDeckTier;
};

/** Encuadre en fracciones (0-1) del arte recortado de la carátula. */
export type CoverCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type MetaDeck = {
  id: number;
  name: string;
  description: string | null;
  archetype: Archetype | null;
  engines: Archetype[];
  banlists: MetaDeckBanlist[];
  banlistCount: number;
  /** Carta que ilustra al mazo (overlay de los videos de replays). */
  coverKonamiId?: number | null;
  coverCard?: { konamiId: number; name: string; image: string | null } | null;
  coverCropSquare?: CoverCrop | null;
  coverCropRect?: CoverCrop | null;
  coverSquareUrl?: string | null;
  coverRectUrl?: string | null;
  /** Arte completo de la carátula; sólo viene en `getMetaDeck`. */
  coverArtUrl?: string | null;
  createdAt: string;
  updatedAt: string;
};

const META_DECK_FRAGMENT = gql`
  fragment MetaDeckFields on MetaDeck {
    id
    name
    description
    archetype {
      id
      name
      type
    }
    engines {
      id
      name
      type
    }
    banlists {
      id
      name
      effectiveDate
      isActive
      formatId
      tier
    }
    banlistCount
    coverKonamiId
    coverCard {
      konamiId
      name
      image
    }
    createdAt
    updatedAt
  }
`;

export const getMetaDecks = async (
  user: Nullable<UserWithToken>,
  filters: {
    archetypeId?: number;
    search?: string;
    page?: number;
    limit?: number;
  } = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<MetaDeck>>(
    gql`
      ${META_DECK_FRAGMENT}
      query metaDecks($filters: MetaDeckFilters) {
        data: metaDecks(filters: $filters) {
          data {
            ...MetaDeckFields
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters }
  );

export const getMetaDeck = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<MetaDeck>>(
    gql`
      ${META_DECK_FRAGMENT}
      query metaDeck($id: Int!) {
        data: metaDeck(id: $id) {
          ...MetaDeckFields
          coverArtUrl
          coverCropSquare {
            x
            y
            width
            height
          }
          coverCropRect {
            x
            y
            width
            height
          }
          coverSquareUrl
          coverRectUrl
        }
      }
    `,
    { id }
  );

export const createMetaDeck = async (
  user: Nullable<UserWithToken>,
  input: {
    name: string;
    archetypeId?: number;
    description?: string;
    engineIds?: number[];
    coverKonamiId?: number | null;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<MetaDeck>>(
    gql`
      ${META_DECK_FRAGMENT}
      mutation createMetaDeck($input: CreateMetaDeckInput!) {
        data: createMetaDeck(input: $input) {
          ...MetaDeckFields
        }
      }
    `,
    { input }
  );

export const updateMetaDeck = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: {
    name?: string;
    archetypeId?: number;
    description?: string;
    engineIds?: number[];
    /** null saca la carátula; sin el campo, no se toca. */
    coverKonamiId?: number | null;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<MetaDeck>>(
    gql`
      ${META_DECK_FRAGMENT}
      mutation updateMetaDeck($id: Int!, $input: UpdateMetaDeckInput!) {
        data: updateMetaDeck(id: $id, input: $input) {
          ...MetaDeckFields
        }
      }
    `,
    { id, input }
  );

export const deleteMetaDeck = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteMetaDeck($id: Int!) {
        data: deleteMetaDeck(id: $id)
      }
    `,
    { id }
  );

/**
 * Carátula por defecto (la carta del arquetipo) a los mazos que no tienen.
 * Devuelve cuántos actualizó; las elegidas a mano no se tocan.
 */
export const assignDefaultMetaDeckCovers = async (
  user: Nullable<UserWithToken>
) =>
  getGraphQLQueryClient(user).request<WithData<number>>(
    gql`
      mutation assignDefaultMetaDeckCovers {
        data: assignDefaultMetaDeckCovers
      }
    `
  );

/**
 * Regenera los recortes de la carátula con el encuadre dado. El que no se manda
 * conserva el guardado.
 */
export const updateMetaDeckCoverCrops = async (
  user: Nullable<UserWithToken>,
  id: number,
  crops: { square?: CoverCrop; rect?: CoverCrop }
) =>
  getGraphQLQueryClient(user).request<WithData<MetaDeck>>(
    gql`
      ${META_DECK_FRAGMENT}
      mutation updateMetaDeckCoverCrops(
        $id: Int!
        $square: CoverCropInput
        $rect: CoverCropInput
      ) {
        data: updateMetaDeckCoverCrops(id: $id, square: $square, rect: $rect) {
          ...MetaDeckFields
          coverCropSquare {
            x
            y
            width
            height
          }
          coverCropRect {
            x
            y
            width
            height
          }
          coverSquareUrl
          coverRectUrl
        }
      }
    `,
    { id, square: crops.square, rect: crops.rect }
  );

/** Fusiona todos los `sourceIds` dentro de `targetId` en una sola pasada. */
export const mergeMetaDecks = async (
  user: Nullable<UserWithToken>,
  sourceIds: number[],
  targetId: number
) =>
  getGraphQLQueryClient(user).request<WithData<MetaDeck>>(
    gql`
      ${META_DECK_FRAGMENT}
      mutation mergeMetaDecks($sourceIds: [Int!]!, $targetId: Int!) {
        data: mergeMetaDecks(sourceIds: $sourceIds, targetId: $targetId) {
          ...MetaDeckFields
        }
      }
    `,
    { sourceIds, targetId }
  );

export const addMetaDeckToBanlist = async (
  user: Nullable<UserWithToken>,
  banlistId: number,
  metaDeckId: number,
  tier?: MetaDeckTier
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation addMetaDeckToBanlist(
        $banlistId: Int!
        $metaDeckId: Int!
        $tier: MetaDeckTier
      ) {
        data: addMetaDeckToBanlist(
          banlistId: $banlistId
          metaDeckId: $metaDeckId
          tier: $tier
        )
      }
    `,
    { banlistId, metaDeckId, tier }
  );

export const updateMetaDeckTier = async (
  user: Nullable<UserWithToken>,
  banlistId: number,
  metaDeckId: number,
  tier: MetaDeckTier
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation updateMetaDeckTier(
        $banlistId: Int!
        $metaDeckId: Int!
        $tier: MetaDeckTier!
      ) {
        data: updateMetaDeckTier(
          banlistId: $banlistId
          metaDeckId: $metaDeckId
          tier: $tier
        )
      }
    `,
    { banlistId, metaDeckId, tier }
  );

export const removeMetaDeckFromBanlist = async (
  user: Nullable<UserWithToken>,
  banlistId: number,
  metaDeckId: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation removeMetaDeckFromBanlist($banlistId: Int!, $metaDeckId: Int!) {
        data: removeMetaDeckFromBanlist(banlistId: $banlistId, metaDeckId: $metaDeckId)
      }
    `,
    { banlistId, metaDeckId }
  );
